// /lib/stores/auth.store.ts
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

interface AuthUIState {
  // UI-only state (NEVER security)
  isLoading: boolean;
  error: string | null;

  // Rate limiting (client hint only)
  loginAttempts: number;
  lastLoginAttempt: number | null;

  // UX helpers
  showSessionWarning: boolean;

  // Actions
  setLoading: (value: boolean) => void;
  setError: (message: string | null) => void;

  recordLoginFailure: () => void;
  resetLoginAttempts: () => void;

  setSessionWarning: (value: boolean) => void;
  reset: () => void;
}

const LOCKOUT_WINDOW_MS = 15 * 60 * 1000;

export const useAuthStore = create<AuthUIState>()(
  persist(
    (set, get) => ({
      isLoading: false,
      error: null,
      loginAttempts: 0,
      lastLoginAttempt: null,
      showSessionWarning: false,

      setLoading: (value) => set({ isLoading: value }),
      setError: (message) => set({ error: message }),

      recordLoginFailure: () => {
        const now = Date.now();
        const { lastLoginAttempt, loginAttempts } = get();

        if (lastLoginAttempt && now - lastLoginAttempt > LOCKOUT_WINDOW_MS) {
          set({ loginAttempts: 1, lastLoginAttempt: now });
        } else {
          set({
            loginAttempts: loginAttempts + 1,
            lastLoginAttempt: now,
          });
        }
      },

      resetLoginAttempts: () =>
        set({
          loginAttempts: 0,
          lastLoginAttempt: null,
          error: null,
        }),

      setSessionWarning: (value) =>
        set({ showSessionWarning: value }),

      reset: () =>
        set({
          isLoading: false,
          error: null,
          loginAttempts: 0,
          lastLoginAttempt: null,
          showSessionWarning: false,
        }),
    }),
    {
      name: "auth-ui-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        loginAttempts: state.loginAttempts,
        lastLoginAttempt: state.lastLoginAttempt,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state?.lastLoginAttempt) return;
        if (Date.now() - state.lastLoginAttempt > LOCKOUT_WINDOW_MS) {
          state.loginAttempts = 0;
          state.lastLoginAttempt = null;
        }
      },
    }
  )
);
